import { useSyncExternalStore } from 'react'

/**
 * Light / dark / system theme.
 *
 * Precedence, highest first:
 *   1. the person's own choice, remembered in this browser
 *   2. the tenant's default from its branding (a reseller can ship light)
 *   3. the operating system's preference
 *
 * "System" is a real choice, not the absence of one: someone who picks it
 * wants the console to follow their OS from light at noon to dark at dusk,
 * and that must keep working after they have made a selection.
 *
 * Implemented as a tiny external store rather than React context so the
 * pre-paint script in main.tsx, the top-bar switcher, the settings page and
 * the login screen all read and write the same state without a provider.
 */

export type ThemeMode = 'light' | 'dark' | 'system'
export type ResolvedTheme = 'light' | 'dark'

const STORAGE_KEY = 'sv.theme'
const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null

let userMode: ThemeMode | null = readStored()
let tenantDefault: ResolvedTheme | null = null
const listeners = new Set<() => void>()

function readStored(): ThemeMode | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY)
    return v === 'light' || v === 'dark' || v === 'system' ? v : null
  } catch {
    return null
  }
}

function effectiveMode(): ThemeMode {
  return userMode ?? tenantDefault ?? 'system'
}

export function resolveMode(mode: ThemeMode): ResolvedTheme {
  if (mode === 'system') return media?.matches ? 'dark' : 'light'
  return mode
}

/** Writes the resolved theme onto <html>, where every token in the
 *  stylesheet keys off it. */
function paint() {
  const resolved = resolveMode(effectiveMode())
  const root = document.documentElement
  root.setAttribute('data-theme', resolved)
  root.style.colorScheme = resolved

  // The browser chrome colour on mobile should match the page, not fight it.
  const meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]')
  if (meta) meta.content = resolved === 'dark' ? '#07090c' : '#f6f8fa'
}

function emit() {
  paint()
  for (const l of listeners) l()
}

// Follow the OS while the effective mode is "system".
media?.addEventListener('change', () => {
  if (effectiveMode() === 'system') emit()
})

/** Call once, before React renders, so the first frame is already themed. */
export function initTheme() {
  paint()
}

export function setThemeMode(mode: ThemeMode) {
  userMode = mode
  try {
    localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    // Blocked storage: the choice still applies for this session.
  }
  emit()
}

/** The tenant's branding default. Applies only until the person chooses. */
export function setTenantDefaultTheme(theme: ResolvedTheme | null | undefined) {
  tenantDefault = theme ?? null
  if (userMode == null) emit()
}

function subscribe(listener: () => void) {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

// The snapshot must be a stable primitive, or useSyncExternalStore loops.
function snapshot() {
  const mode = effectiveMode()
  return `${mode}:${resolveMode(mode)}`
}

export function useTheme() {
  const snap = useSyncExternalStore(subscribe, snapshot, snapshot)
  const [mode, resolved] = snap.split(':') as [ThemeMode, ResolvedTheme]
  return { mode, resolved, setMode: setThemeMode }
}
